import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export function Card({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-card border border-border bg-surface shadow-card',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-b border-border px-5 py-4',
        'sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-text-primary">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-[13px] text-text-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cn('px-5 py-4', className)} {...props}>
      {children}
    </div>
  );
}

/** Tarjeta de metrica para el dashboard y las bandejas de soporte. */
export function StatCard({
  label,
  value,
  icon,
  tone = 'default',
  onClick,
}: {
  label: string;
  value: number | string;
  icon?: ReactNode;
  tone?: 'default' | 'primary' | 'warning' | 'success' | 'danger';
  onClick?: () => void;
}): JSX.Element {
  const tones = {
    default: 'text-text-secondary',
    primary: 'text-primary-bright',
    warning: 'text-warning',
    success: 'text-success',
    danger: 'text-danger',
  } as const;

  const Element = onClick ? 'button' : 'div';

  return (
    <Element
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'rounded-card border border-border bg-surface p-4 text-left shadow-card transition-colors',
        onClick && 'hover:border-border-strong hover:bg-surface-hover',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] font-medium text-text-muted">{label}</span>
        {icon ? <span className={cn('shrink-0', tones[tone])}>{icon}</span> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-text-primary">{value}</p>
    </Element>
  );
}
